import { AppError } from '../errors/app-error.js';
// ─── Fallecido en el sistema de origen ──────────────────────────────────────
// Una persona importada que el sistema de origen registra como fallecida. Su
// expediente se conserva y se muestra con un aviso; NUNCA recibe una cuenta ni
// una invitación: todo camino que crea acceso para una persona lo comprueba.
//
// LA ETIQUETA ES UN DATO DEL ORIGEN, NO UNA ETIQUETA MÁS. Quitarla le devolvería
// a la persona la posibilidad de recibir una cuenta; ponerla le quitaría el
// acceso a alguien vivo. Por eso NINGÚN camino corriente la agrega ni la quita:
// `create` la rechaza, `update` rechaza cambiarla salvo con permiso explícito
// (solo platformAdmin, decidido por quien llama), y las sincronizaciones —CRM—
// la conservan tal como está (`keepSourceTags`). El importador la escribe con
// `reconstitute`, que es cargar un dato, no crearlo.
export const DECEASED_IN_SOURCE_TAG = 'deceased-in-source';
/** Acepta los `tags` de la entidad o los de un documento crudo de Firestore. */
export function isDeceasedInSource(tags) {
    return Array.isArray(tags) && tags.includes(DECEASED_IN_SOURCE_TAG);
}
/** La razón i18n de todo rechazo por intentar mover la etiqueta. */
export const DECEASED_TAG_REASON = 'person.deceased_tag_platform_only';
const deceasedTagError = () => AppError.forbidden('Only a platform admin can add or remove the deceased-in-source tag', DECEASED_TAG_REASON);
/** Las etiquetas entrantes, con la de fallecido EXACTAMENTE como estaba: para
 *  las sincronizaciones (CRM ↔ persona), que no deben mover un dato del origen
 *  ni fallar por él. */
export function keepSourceTags(current, incoming) {
    const rest = incoming.filter((t) => t !== DECEASED_IN_SOURCE_TAG);
    return isDeceasedInSource(current) ? [...rest, DECEASED_IN_SOURCE_TAG] : rest;
}
export class Person {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (!params.firstName.trim())
            throw new Error('First name cannot be empty');
        if (!params.lastName.trim())
            throw new Error('Last name cannot be empty');
        // Lifecycle types (student/alumni/…) and institutional roles (docente/…) are
        // orthogonal: a docente or staff member has a cargo but no academic cycle. A
        // person just needs at least one classification of either kind.
        if (params.types.length === 0 && !params.institutionalRoles?.length) {
            throw new Error('Person must have at least one type or institutional role');
        }
        if (isDeceasedInSource(params.tags))
            throw deceasedTagError();
        const now = new Date();
        return new Person({
            ...params,
            firstName: params.firstName.trim(),
            lastName: params.lastName.trim(),
            institutionalRoles: params.institutionalRoles ?? [],
            tags: params.tags || [],
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new Person(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get firstName() {
        return this.props.firstName;
    }
    get lastName() {
        return this.props.lastName;
    }
    get fullName() {
        return `${this.props.firstName} ${this.props.lastName}`;
    }
    get email() {
        return this.props.email;
    }
    get phone() {
        return this.props.phone;
    }
    get birthDate() {
        return this.props.birthDate;
    }
    get types() {
        return [...this.props.types];
    }
    get institutionalRoles() {
        return [...(this.props.institutionalRoles ?? [])];
    }
    get tags() {
        return [...this.props.tags];
    }
    get nationalId() {
        return this.props.nationalId;
    }
    get studentCode() {
        return this.props.studentCode;
    }
    get origin() {
        return this.props.origin;
    }
    get nationality() {
        return this.props.nationality;
    }
    get organizationName() {
        return this.props.organizationName;
    }
    get address() {
        return this.props.address;
    }
    get notes() {
        return this.props.notes;
    }
    get createdAt() {
        return this.props.createdAt;
    }
    get updatedAt() {
        return this.props.updatedAt;
    }
    get deletedAt() {
        return this.props.deletedAt;
    }
    isDeleted() {
        return this.props.deletedAt !== undefined;
    }
    hasType(type) {
        return this.props.types.includes(type);
    }
    hasInstitutionalRole(role) {
        return (this.props.institutionalRoles ?? []).includes(role);
    }
    addInstitutionalRole(role) {
        const current = this.props.institutionalRoles ?? [];
        if (current.includes(role))
            return this;
        return new Person({
            ...this.props,
            institutionalRoles: [...current, role],
            updatedAt: new Date(),
        });
    }
    removeInstitutionalRole(role) {
        return new Person({
            ...this.props,
            institutionalRoles: (this.props.institutionalRoles ?? []).filter((r) => r !== role),
            updatedAt: new Date(),
        });
    }
    /** Le pone su código de alumno (H6). UN CÓDIGO NO SE REESCRIBE NUNCA: está
     *  impreso en documentos y en la memoria de la gente. Si ya tiene uno, error. */
    assignStudentCode(code) {
        if (this.props.studentCode) {
            throw new Error('This person already has a student code; it is never rewritten');
        }
        return new Person({ ...this.props, studentCode: code, updatedAt: new Date() });
    }
    addType(type) {
        if (this.props.types.includes(type))
            return this;
        return new Person({ ...this.props, types: [...this.props.types, type], updatedAt: new Date() });
    }
    removeType(type) {
        return new Person({
            ...this.props,
            types: this.props.types.filter((t) => t !== type),
            updatedAt: new Date(),
        });
    }
    /**
     * Sets the person's lifecycle stage, removing all other lifecycle stages.
     * Order: externo → prospecto-académico → student (Estudiante) → alumni (Egresado)
     * Accepts any valid lifecycle direction (including reverting to 'externo' on rejection).
     * Non-lifecycle types (e.g., 'faculty', 'staff', 'contact') are preserved.
     */
    promoteToLifecycleStage(stage) {
        const LIFECYCLE_STAGES = ['externo', 'prospecto-académico', 'student', 'alumni'];
        const nonLifecycleTypes = this.props.types.filter((t) => !LIFECYCLE_STAGES.includes(t));
        const newTypes = [...nonLifecycleTypes, stage];
        return new Person({ ...this.props, types: newTypes, updatedAt: new Date() });
    }
    addTag(tag) {
        const trimmed = tag.trim().toLowerCase();
        if (this.props.tags.includes(trimmed))
            return this;
        if (trimmed === DECEASED_IN_SOURCE_TAG)
            throw deceasedTagError();
        return new Person({
            ...this.props,
            tags: [...this.props.tags, trimmed],
            updatedAt: new Date(),
        });
    }
    removeTag(tag) {
        const trimmed = tag.trim().toLowerCase();
        if (trimmed === DECEASED_IN_SOURCE_TAG && this.props.tags.includes(trimmed)) {
            throw deceasedTagError();
        }
        return new Person({
            ...this.props,
            tags: this.props.tags.filter((t) => t !== trimmed),
            updatedAt: new Date(),
        });
    }
    update(updates, 
    /** Solo quien llama sabe si es platformAdmin; sin esto, mover la etiqueta
     *  de fallecido se rechaza. */
    opts = {}) {
        if (updates.tags !== undefined &&
            isDeceasedInSource(updates.tags) !== isDeceasedInSource(this.props.tags) &&
            !opts.allowDeceasedTagChange) {
            throw deceasedTagError();
        }
        return new Person({ ...this.props, ...updates, updatedAt: new Date() });
    }
    softDelete() {
        if (this.isDeleted())
            throw new Error('Person is already deleted');
        return new Person({ ...this.props, deletedAt: new Date(), updatedAt: new Date() });
    }
    restore() {
        if (!this.isDeleted())
            throw new Error('Person is not deleted');
        const { deletedAt: _, ...rest } = this.props;
        return new Person({ ...rest, updatedAt: new Date() });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
//# sourceMappingURL=person.entity.js.map